import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ParameterState } from './parameter.state';

export const selectParameterState = createFeatureSelector<ParameterState>('parameter');

export const selectAllParameters = createSelector(
  selectParameterState,
  (state: ParameterState) => state.parameters
);

export const selectSelectedParameter = createSelector(
  selectParameterState,
  (state: ParameterState) => state.selectedParameter
);

export const selectParameterDetails = createSelector(
  selectSelectedParameter,
  parameter => parameter?.parametroDetalles || []
);

export const selectParameterLoading = createSelector(
  selectParameterState,
  (state: ParameterState) => state.loading
);

export const selectParameterError = createSelector(
  selectParameterState,
  (state: ParameterState) => state.error
);

export const selectParameterCreating = createSelector(
  selectParameterState,
  (state: ParameterState) => state.creating
);

export const selectParameterUpdating = createSelector(
  selectParameterState,
  (state: ParameterState) => state.updating
);

export const selectActiveParameters = createSelector(selectAllParameters, parameters =>
  parameters.filter(p => p.bEstado)
);

export const selectInactiveParameters = createSelector(selectAllParameters, parameters =>
  parameters.filter(p => !p.bEstado)
);

export const selectParameterById = (id: number) =>
  createSelector(selectAllParameters, parameters => parameters.find(p => p.idParametro === id));

export const selectParameterDetailById = (parameterId: number, detailId: number) =>
  createSelector(selectParameterById(parameterId), parameter =>
    parameter?.parametroDetalles?.find(d => d.idParametroDetalle === detailId)
  );

export const selectParameterByIdLoading = createSelector(
  selectParameterState,
  (state: ParameterState) => state.loadingParameterById
);

export const selectActiveParametersLoading = createSelector(
  selectParameterState,
  (state: ParameterState) => state.loadingActiveParameters
);

export const selectInactiveParametersLoading = createSelector(
  selectParameterState,
  (state: ParameterState) => state.loadingInactiveParameters
);

// Cache selectors
export const selectCacheValid = createSelector(
  selectParameterState,
  (state: ParameterState) => state.cacheValid
);

export const selectLastFetched = createSelector(
  selectParameterState,
  (state: ParameterState) => state.lastFetched
);

export const selectCacheExpiry = createSelector(
  selectParameterState,
  (state: ParameterState) => state.cacheExpiry
);

export const selectForceRefresh = createSelector(
  selectParameterState,
  (state: ParameterState) => state.forceRefresh
);

export const selectShouldFetchData = createSelector(
  selectCacheValid,
  selectLastFetched,
  selectCacheExpiry,
  selectForceRefresh,
  (cacheValid, lastFetched, cacheExpiry, forceRefresh) => {
    if (forceRefresh) return true;
    if (!cacheValid) return true;
    if (!lastFetched || !cacheExpiry) return true;
    return Date.now() > cacheExpiry;
  }
);

export const selectCacheStatus = createSelector(
  selectCacheValid,
  selectLastFetched,
  selectCacheExpiry,
  selectForceRefresh,
  (cacheValid, lastFetched, cacheExpiry, forceRefresh) => ({
    isValid: cacheValid,
    lastFetched: lastFetched ? new Date(lastFetched) : null,
    expiresAt: cacheExpiry ? new Date(cacheExpiry) : null,
    forceRefresh,
    timeUntilExpiry: cacheExpiry ? cacheExpiry - Date.now() : null
  })
);
